import auth from "../../utils/authentication";
import {ApiCallError} from "../torrestream-api/torresstream-api";

export class ApiFunction {

    private functionName: string;
    private backend: string;

    constructor(functionName, backend) {
        this.functionName = functionName;
        this.backend = backend;
    }

    async call(params?: Record<string, any>) {
        if (!this.functionName) {
            throw new Error("Function name not set");
        }
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;

        const headers = {
            'Content-Type': 'application/json',
        }

        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        }

        const response = await fetch(`${this.backend}${this.functionName}`, {
            body: JSON.stringify(params),
            method: "POST",
            headers,
        })
        if (response.ok) {
            if (response.headers.get('content-type')?.includes('application/json')) {
                return response.json();
            }
            return response;
        } else {
            throw new ApiCallError(await response.text(), response.status);
        }
    }

    getUrl(params) {
        return `${this.backend}${this.functionName}?${new URLSearchParams(params).toString()}`;
    }
}

class ChatbotApiError extends Error {

    public code: string;

    constructor(message, code?: string) {
        super(message);
        this.name = "ChatbotApiError";
        this.code = code;
    }
}

export class ChatbotApi {
    static baseUrl = 'https://torresowo.myftp.org:7777/towers-chatbot/api/v2/';

    // Obtener todos los usuarios
    static async getAllUsers() {
        const fn = new ApiFunction('getAllUsers', this.baseUrl);
        return fn.call({});
    }

    // Actualizar permisos de un usuario
    static async updateUserPermissions(userId, permissions) {
        const fn = new ApiFunction('updateUserPermissions', this.baseUrl);
        return fn.call({userId, permissions});
    }

    // Eliminar usuario
    static async deleteUser(userId) {
        const fn = new ApiFunction('deleteUser', this.baseUrl);
        return fn.call({userId});
    }

    static async updateUserPreference({key, value}) {
        const fn = new ApiFunction('updateUserPreference', this.baseUrl);
        return fn.call({key, value});
    }

    static async getUserPreferences() {
        const fn = new ApiFunction('getUserPreferences', this.baseUrl);
        return fn.call({});
    }

    static async getUserRights(userUid) {
        const getUserRights = new ApiFunction('getUserPermissions', this.baseUrl);
        return getUserRights.call({userId: userUid});
    }

    static async getConversationChatbot(chatId) {
        const getConversation = new ApiFunction('ai-getConversation', this.baseUrl);
        return getConversation.call({chatId});
    }

    static async getAllConversations() {
        const getConversations = new ApiFunction('ai-getAllConversations', this.baseUrl);
        return getConversations.call({});
    }

    /**
     * Llama al endpoint de chat usando POST y streaming SSE
     * @param message Mensaje del usuario
     * @param onEvent Callback que recibe eventos {type: 'start'|'chunk'|'finish'|'done', data: any}
     * @param onError Callback para errores
     */
    static async chat({input, chatId}, onEvent, onError) {
        try {
            const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
            if (!token) throw new Error("No auth token");
            const res = await fetch(`${this.baseUrl}ai-chat`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({input, chatId}),
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new ChatbotApiError(`HTTP ${res.status}: ${errorText}`);
            }

            if (!res.body) throw new Error("No response body");

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const {done, value} = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, {stream: true});

                // SSE viene separado por doble salto de línea
                const parts = buffer.split("\n\n");
                buffer = parts.pop(); // lo que queda incompleto

                for (const part of parts) {
                    const lines = part.split("\n");
                    let eventName = "message";
                    let dataPayload = "";

                    for (const line of lines) {
                        if (line.startsWith("event:")) {
                            eventName = line.replace("event:", "").trim();
                        } else if (line.startsWith("data:")) {
                            dataPayload += line.replace("data:", "").trim();
                        }
                    }

                    if (!dataPayload) continue;

                    if (dataPayload === "[DONE]") {
                        onEvent({type: "done", data: null});
                        return;
                    }

                    try {
                        const parsed = JSON.parse(dataPayload);

                        if (eventName === "start") {
                            onEvent({type: "start", data: parsed});
                        } else if (eventName === "chunk") {
                            onEvent({type: "chunk", data: parsed});
                        } else if (eventName === "finish") {
                            onEvent({type: "finish", data: parsed});
                        } else {
                            onEvent({type: eventName, data: parsed});
                        }
                    } catch {
                        // si no es JSON, envía texto plano
                        onEvent({type: eventName, data: dataPayload});
                    }
                }
            }
        } catch (err) {
            console.error("Chat API Error:", err);
            if (onError) onError(err);
        }
    }
}
